import React, { useEffect, useState } from "react";
import { FileText, Download, FileSpreadsheet, FileImage, File as FileIcon, FolderOpen } from "lucide-react";
import { api, API } from "../lib/api";

const typeIcon = (ct = "", name = "") => {
  const s = `${ct} ${name}`.toLowerCase();
  if (s.includes("pdf")) return FileText;
  if (s.includes("sheet") || s.includes("excel") || s.includes("csv") || s.endsWith(".xls") || s.endsWith(".xlsx")) return FileSpreadsheet;
  if (s.includes("image") || /\.(jpg|jpeg|png|webp|gif)$/.test(s)) return FileImage;
  return FileIcon;
};

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentsPage = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([])).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <section className="bg-[#0F2942] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-widest font-semibold text-amber-400">Transparency</span>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-extrabold text-white">Official Documents</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">
            Download our registration certificates, reports and other official documents. We believe in
            complete transparency with our donors and well-wishers.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="documents-page">
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-slate-100 animate-pulse" />)}
          </div>
        ) : docs.length === 0 ? (
          <div className="text-center py-24 text-slate-400">
            <FolderOpen className="h-14 w-14 mx-auto mb-4" />
            <p className="font-medium">No documents published yet.</p>
            <p className="text-sm">Official documents will appear here once uploaded.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {docs.map((d) => {
              const Icon = typeIcon(d.content_type, d.original_filename || d.name);
              return (
                <div
                  key={d.id}
                  data-testid={`document-row-${d.id}`}
                  className="flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="h-12 w-12 rounded-xl bg-blue-50 ring-4 ring-blue-100/60 flex items-center justify-center shrink-0">
                    <Icon className="h-6 w-6 text-blue-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900 truncate">{d.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {d.original_filename}{d.size ? ` · ${fmtSize(d.size)}` : ""} · Added {new Date(d.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <a
                    href={`${API}/documents/${d.id}/download`}
                    data-testid={`document-download-${d.id}`}
                    className="shrink-0 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 sm:px-5 py-2.5 rounded-full transition-all"
                  >
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">Download</span>
                  </a>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default DocumentsPage;
